/**
 * „Prijavi se kao korisnik" - strana podrške (ADR-044).
 *
 * Postoji zbog problema koji se ne vidi spolja: greška vezana za **taj** nalog
 * - njegove licence, njegove operatere, njegov katalog - ne reprodukuje se sa
 * naloga podrške. Do sada je jedini način bio tražiti korisniku lozinku, što je
 * gore od svega ovoga.
 *
 * Tri stvari koje ovaj ekran mora da učini očiglednim:
 *   1. **razlog je obavezan** i klijent ga vidi;
 *   2. pristup **ističe** i može da se prekine pre isteka;
 *   3. iz njega se **ne izdaju dokumenti** - spisak zabranjenih radnji dolazi
 *      sa servera, da ekran ne tvrdi nešto što server ne sprovodi.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Ban, KeyRound, LifeBuoy, RefreshCw, X } from 'lucide-react';
import { api } from '../../../api/client';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';

type Target = {
  id: string;
  username: string;
  fullName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
};

type Session = {
  id: string;
  userId: string;
  username: string | null;
  fullName: string | null;
  sysadminEmail: string;
  reason: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  endedBy: string;
  ip: string;
  live: boolean;
};

type Limits = { ttlMinutes: number; blockedPaths: string[] };

/**
 * Gde stoji aplikacija klijenta.
 *
 * U razvoju panel radi na :5174 a aplikacija na :1420, pa se odredište ne može
 * izvesti iz `location.origin`. Na produkciji su različiti poddomeni, pa isto
 * važi - zato podešavanje, sa razumnim podrazumevanim vrednostima.
 */
const APP_URL = (import.meta as any).env?.VITE_APP_URL
  || (window.location.hostname === 'localhost' ? 'http://localhost:1420' : 'https://pos.efikasa.rs');

function trenutak(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.toLocaleDateString('sr-RS')} ${d.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })}`;
}

export function ImpersonationTab({ tenantId }: { tenantId: string }) {
  const [targets, setTargets] = useState<Target[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [limits, setLimits] = useState<Limits | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);
  const [dijalog, setDijalog] = useState<{ user: Target; razlog: string } | null>(null);
  const [salje, setSalje] = useState(false);
  useEscapeToClose(() => { if (!salje) setDijalog(null); }, !!dijalog);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, s] = await Promise.all([
        api.get<{ users: Target[] }>(`/admin/tenants/${tenantId}/impersonation-targets`),
        api.get<{ sessions: Session[] }>(`/admin/tenants/${tenantId}/impersonations`),
      ]);
      setTargets(t.users ?? []);
      setSessions(s.sessions ?? []);
    } catch (e: any) {
      setError(e?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    api.get<Limits>('/admin/tenants/impersonation/limits')
      .then(setLimits)
      .catch(() => { /* ekran radi i bez spiska ogranicenja */ });
  }, []);

  const udji = async () => {
    if (!dijalog || salje) return;
    if (!dijalog.razlog.trim()) { setError('Razlog je obavezan.'); return; }
    setSalje(true);
    setError(null);
    try {
      const odgovor = await api.post<Record<string, unknown>>(
        `/admin/tenants/${tenantId}/users/${dijalog.user.id}/impersonate`,
        { reason: dijalog.razlog.trim() },
      );

      /**
       * Ceo odgovor ide aplikaciji kroz **heš adrese**, ne kroz upit: heš se ne
       * šalje serveru i ne završava u dnevnicima posrednika. Aplikacija ga
       * odmah briše iz adrese.
       */
      const payload = btoa(unescape(encodeURIComponent(JSON.stringify(odgovor))));
      window.open(`${APP_URL}/support-access#payload=${payload}`, '_blank', 'noopener');

      setDijalog(null);
      setPoruka(`Pristup otvoren. Ističe ${trenutak(String(odgovor.expiresAt))}.`);
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Pristup nije otvoren');
    } finally {
      setSalje(false);
    }
  };

  const prekini = async (s: Session) => {
    setError(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/impersonations/${s.id}/end`, {});
      setPoruka('Pristup je prekinut.');
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Prekid nije upisan');
    }
  };

  const uToku = sessions.filter((s) => s.live);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--color-text-strong)' }}>
          Pristup nalogu korisnika
        </h3>
        <button onClick={() => void refresh()} style={btnGhost}><RefreshCw size={13} /> Osveži</button>
      </div>

      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--color-text-muted)', maxWidth: 760 }}>
        Otvara se <strong>privremen pristup</strong> nalogu korisnika, bez njegove lozinke - za probleme koji
        se ne vide sa naloga podrške. Razlog je obavezan i ostaje zabeležen.
        {limits && <> Pristup ističe posle <strong>{limits.ttlMinutes} min</strong>.</>}
        {' '}Iz njega se <strong>ne mogu izdavati dokumenti</strong> (fiskalni račun, eFaktura, otpremnica,
        avans) ni plaćati - te radnje server odbija.
      </p>

      {error && <div style={upozorenje}><AlertTriangle size={14} /> {error}</div>}
      {poruka && <div style={obavestenje}>{poruka}</div>}
      {loading && <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Učitavanje…</p>}

      {uToku.length > 0 && (
        <section style={{ ...kartica, borderColor: '#f59e0b', background: 'rgba(245,158,11,0.08)', marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#b45309', display: 'flex', alignItems: 'center', gap: 6 }}>
            <LifeBuoy size={14} /> Pristup u toku
          </h4>
          {uToku.map((s) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
              <span>
                <strong>{s.sysadminEmail}</strong> u nalogu <strong>{s.fullName || s.username}</strong>
                {' '}- ističe {trenutak(s.expiresAt)} · „{s.reason}"
              </span>
              <button onClick={() => void prekini(s)} style={{ ...btnDanger, marginLeft: 'auto' }}>
                <Ban size={13} /> Prekini
              </button>
            </div>
          ))}
        </section>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 24 }}>
        <thead>
          <tr>{['Korisnik', 'Uloga', 'Poslednja prijava', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {targets.map((u) => (
            <tr key={u.id}>
              <td style={td}>
                <strong style={{ color: 'var(--color-text-strong)' }}>{u.fullName || u.username}</strong>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{u.username}</div>
              </td>
              <td style={td}>{u.role}{u.isActive ? '' : ' · neaktivan'}</td>
              <td style={td}>{trenutak(u.lastLoginAt)}</td>
              <td style={{ ...td, textAlign: 'right' }}>
                <button
                  onClick={() => { setDijalog({ user: u, razlog: '' }); setError(null); }}
                  disabled={!u.isActive}
                  style={{ ...btnPrimary, opacity: u.isActive ? 1 : 0.5 }}
                >
                  <KeyRound size={13} /> Prijavi se kao
                </button>
              </td>
            </tr>
          ))}
          {targets.length === 0 && !loading && (
            <tr><td style={td} colSpan={4}>Klijent nema nijednog korisnika.</td></tr>
          )}
        </tbody>
      </table>

      <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)' }}>
        Istorija pristupa
      </h4>
      {sessions.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Nijedan pristup nije zabeležen.</p>
      ) : (
        <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4, fontSize: 12, color: 'var(--color-text-muted)' }}>
          {sessions.map((s) => (
            <li key={s.id}>
              {trenutak(s.startedAt)} - <strong>{s.sysadminEmail}</strong> u nalogu {s.fullName || s.username}
              {' · '}„{s.reason}"
              {s.endedAt ? ` · završen ${trenutak(s.endedAt)}${s.endedBy ? ` (${s.endedBy})` : ''}` : (s.live ? ' · U TOKU' : ' · istekao')}
              {s.ip ? ` · ${s.ip}` : ''}
            </li>
          ))}
        </ol>
      )}

      {dijalog && (
        <div style={overlay}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <KeyRound size={16} />
              <strong style={{ color: 'var(--color-text-strong)' }}>
                Prijava kao {dijalog.user.fullName || dijalog.user.username}
              </strong>
              <button onClick={() => setDijalog(null)} style={{ ...btnGhost, marginLeft: 'auto' }}><X size={14} /></button>
            </header>

            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--color-text-muted)' }}>
              Otvara se privremen pristup nalogu ovog korisnika. <strong>Lozinka se ne saznaje i ne menja.</strong>
              {' '}Pristup je zabeležen sa vašim imenom i razlogom, i iz njega se ne mogu izdavati dokumenti.
            </p>

            <label style={oznaka}>Razlog (obavezno)</label>
            <input
              value={dijalog.razlog}
              onChange={(e) => setDijalog({ ...dijalog, razlog: e.target.value })}
              placeholder="npr. prijava #124 - račun se ne fiskalizuje na ovoj lokaciji"
              style={polje}
              autoFocus
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
              <button type="button" onClick={() => setDijalog(null)} style={{ ...btnGhost, width: '100%', textAlign: 'center' }} disabled={salje}>Otkaži</button>
              <button onClick={() => void udji()} style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }} disabled={salje}>
                {salje ? 'Otvaram…' : 'Otvori pristup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const kartica: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 8, padding: 14,
};

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--color-text-muted)',
  borderBottom: '1px solid var(--color-border)',
};

const td: React.CSSProperties = { padding: '10px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top' };

const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-muted)',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: 'none', background: 'var(--color-primary, #2563eb)', color: '#fff', fontWeight: 600,
};

const btnDanger: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: '1px solid #ef4444', background: 'rgba(239,68,68,0.08)', color: '#b91c1c',
};

const upozorenje: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 12px', borderRadius: 6, marginBottom: 12,
  background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontSize: 13,
};

const obavestenje: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 6, marginBottom: 12,
  background: 'rgba(22,163,74,0.10)', color: '#15803d', fontSize: 13,
};

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20,
};

const modal: React.CSSProperties = {
  width: '100%', maxWidth: 520, borderRadius: 10, padding: 20,
  background: 'var(--color-bg-surface, #fff)', border: '1px solid var(--color-border)',
};

const oznaka: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, margin: '10px 0 4px', color: 'var(--color-text-strong)',
};

const polje: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
  border: '1px solid var(--color-border)', background: 'var(--color-bg-surface, #fff)',
  color: 'var(--color-text-strong)', boxSizing: 'border-box',
};
